import { useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { request } from '../api/client'

interface CaptchaConfig { backend: string; site_key: string; expires_in: number }
interface TurnstileOptions {
  sitekey: string
  action: string
  size: 'normal' | 'compact'
  callback: (token: string) => void
  'expired-callback': () => void
  'error-callback': () => void
}
interface Turnstile {
  render(element: HTMLElement, options: TurnstileOptions): string
  remove(id: string): void
}
declare global { interface Window { turnstile?: Turnstile } }
let loading: Promise<Turnstile> | undefined
function loadTurnstile(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  loading ??= new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.onload = () => window.turnstile ? resolve(window.turnstile) : reject(new Error('unavailable'))
    script.onerror = () => { loading = undefined; reject(new Error('unavailable')) }
    document.head.appendChild(script)
  })
  return loading
}

/** Public runtime configuration only. The browser never receives a secret key. */
export function Captcha({ action, token, resetKey, onToken }: {
  action: 'admin_login' | 'client_otp_send' | 'client_otp_resend'
  token: string | null
  resetKey: number
  onToken: (token: string | null) => void
}) {
  const small = useMediaQuery('(max-width:400px)')
  const [config, setConfig] = useState<CaptchaConfig | null>(null)
  const [error, setError] = useState(false)
  const [pending, setPending] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const inFlight = useRef(false)
  useEffect(() => {
    let active = true
    void request<CaptchaConfig>('/auth/captcha/').then(value => { if(active) setConfig(value) }).catch(() => { if(active) setError(true) })
    return () => { active = false }
  }, [])
  useEffect(() => {
    if(config?.backend !== 'turnstile' || !container.current) return
    let active = true
    let widget: string | undefined
    let provider: Turnstile | undefined
    void loadTurnstile().then(value => {
      if(!active || !container.current) return
      provider = value
      widget = value.render(container.current, {
        sitekey: config.site_key, action, size:small?'compact':'normal',
        callback: value => { if(active) { setError(false); onToken(value) } },
        'expired-callback': () => { if(active) onToken(null) },
        'error-callback': () => { if(active) { onToken(null); setError(true) } },
      })
    }).catch(() => { if(active) setError(true) })
    return () => { active = false; if(widget !== undefined) provider?.remove(widget) }
  }, [action, config, resetKey, onToken, small])
  async function solveTest() {
    if(inFlight.current) return
    inFlight.current = true; setPending(true); setError(false)
    try {
      const result = await request<{ captcha_token: string }>('/auth/captcha/', { method: 'POST', body: { action } })
      onToken(result.captcha_token)
    } catch { onToken(null); setError(true) }
    finally { inFlight.current = false; setPending(false) }
  }
  return <Box role="group" aria-label="تأیید امنیتی" sx={{ minHeight: 70 }}>
    <Typography variant="body2">{action === 'client_otp_resend' ? 'تأیید امنیتی برای ارسال دوبارهٔ کد' : 'تأیید امنیتی'}</Typography>
    {error ? <Alert severity="error" role="alert">تأیید امنیتی در دسترس نیست. دوباره تلاش کنید.</Alert> : null}
    <Box ref={container} dir="ltr" sx={{ maxWidth: '100%', overflow: 'auto' }} />
    {config?.backend === 'development' ? <>
      <Typography variant="caption">فقط محیط آزمون؛ پیامک واقعی ارسال نمی‌شود.</Typography>
      <Button type="button" onClick={() => void solveTest()} disabled={pending || !!token}>{token ? 'تأیید انجام شد' : pending ? 'در حال تأیید…' : 'انجام تأیید امنیتی آزمایشی'}</Button>
    </> : null}
  </Box>
}
